﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as api from "~base/Services/API";
import * as sessionModel from "~base/Services/API/Session/Model";

//-------------------------------------------------------------------------------------
// Http Contract
//-------------------------------------------------------------------------------------

const uri = (
    subjectIdentifier: string,
    sessionTemplateFqn: string,
    state?: sessionModel.SessionState,
    orderBy?: sessionModel.SessionOrderBy,
    orderDir?: sessionModel.SessionOrderDir,
    offset?: number,
    limit?: number,
    salesOpportunity?: string
): string => {
    let uri = `api/Guide/v1/Sessions?offset=${offset ?? "0"}`;

    if (limit) {
        uri += `&limit=${limit}`;
    }

    if (subjectIdentifier) {
        // There should be a better way to handle this. Currently the API assumes that the primary subject identifier is the one to use.
        // In jRåd we should phase out fake civic registration number and replace the primary (in config) with Email. For now we rely on this instead.
        if (
            subjectIdentifier.indexOf("|") == -1 &&
            subjectIdentifier.indexOf("@") != -1
        ) {
            uri += `&subjectIdentifier=${encodeURIComponent("VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson.Epost|" + subjectIdentifier)}`;
        }

        uri += `&subjectIdentifier=${encodeURIComponent(subjectIdentifier)}`;
    }

    if (sessionTemplateFqn) {
        uri += `&sessionTemplateFqn=${encodeURIComponent(sessionTemplateFqn)}`;
    }

    if (state) {
        uri += `&state=${encodeURIComponent(state)}`;
    }

    if (orderBy) {
        uri += `&orderBy=${encodeURIComponent(orderBy)}`;
    }

    if (orderDir) {
        uri += `&orderDir=${encodeURIComponent(orderDir)}`;
    }

    if (salesOpportunity) {
        uri += `&salesOpportunity=${encodeURIComponent(salesOpportunity)}`;
    }

    return uri;
};
const method = "GET";

//-------------------------------------------------------------------------------------
// Types
//-------------------------------------------------------------------------------------

export interface GetSessionsContext {
    subjectIdentifier?: string;
    sessionTemplateFqn?: string;
    state?: sessionModel.SessionState;
    orderBy?: sessionModel.SessionOrderBy;
    orderDir?: sessionModel.SessionOrderDir;
    offset?: number;
    limit?: number;
    salesOpportunity?: string;
}

interface GetSessionsProps
    extends
        GetSessionsContext,
        api.ApiHookBaseProps<
            unknown,
            sessionModel.SessionsResponse,
            GetSessionsContext
        > {}

//-------------------------------------------------------------------------------------
// API Hook
//-------------------------------------------------------------------------------------

export function useGetSessions(
    props: GetSessionsProps
): [boolean, (params: GetSessionsContext) => Promise<unknown>] {
    const apiParams: api.ApiHookProps<
        unknown,
        sessionModel.SessionsResponse,
        unknown
    > = {
        ...props,
        uri: uri(
            props.subjectIdentifier,
            props.sessionTemplateFqn,
            props.state,
            props.orderBy,
            props.orderDir,
            props.offset,
            props.limit,
            props.salesOpportunity
        ),
        method: method,
    };

    const [loading, invoker] = api.useApi<
        unknown,
        sessionModel.SessionsResponse,
        unknown
    >(apiParams);
    return [
        loading,
        (params: GetSessionsContext) =>
            invoker(
                uri(
                    params.subjectIdentifier,
                    params.sessionTemplateFqn,
                    params.state,
                    params.orderBy,
                    params.orderDir,
                    params.offset,
                    params.limit,
                    params.salesOpportunity
                ),
                undefined,
                params
            ),
    ];
}
